import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useT } from "@/i18n";
import LangSelector from "./LangSelector";
import ThemeToggle from "./ThemeToggle";
import { useLandingTheme } from "@/context/LandingThemeContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d216ca66-0733-41eb-aefb-a68564ba10f0/artifacts/fn20k37z_FINAL%20Logo%20Newsky%20png.png";

export default function Navbar() {
  const t = useT();
  const { theme: landingTheme } = useLandingTheme();
  const L = landingTheme === "light";

  const navLinks = [
    { label: t.nav.contact, href: "#contato" },
    { label: t.nav.support, href: "/support" },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      setMobileOpen(false);
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? L ? "bg-white/80 backdrop-blur-xl border-b border-black/5 shadow-sm" : "bg-slate-950/80 backdrop-blur-xl border-b border-white/10"
          : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between h-20 sm:h-24">
        <a href="/" data-testid="navbar-logo" className="flex items-center">
          <img src={LOGO_URL} alt="NewSky Mobile" className="h-12 sm:h-14 w-auto" />
        </a>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-3">
          {/* Services dropdown */}
          <div ref={servicesRef} className="relative">
            <button onClick={() => setServicesOpen(!servicesOpen)}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-base font-semibold transition-all duration-300 hover:scale-[1.04] whitespace-nowrap"
              style={{ color: L ? "rgba(0,0,0,0.8)" : "#ffffff" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = L ? "#ea580c" : "#f97316"; e.currentTarget.style.background = L ? "rgba(249,115,22,0.06)" : "rgba(249,115,22,0.1)"; }}
              onMouseLeave={(e) => { if (!servicesOpen) { e.currentTarget.style.color = L ? "rgba(0,0,0,0.8)" : "#ffffff"; e.currentTarget.style.background = "transparent"; } }}>
              {t.nav.services} <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div className="absolute left-0 top-full mt-2 w-60 rounded-2xl overflow-hidden z-50"
                style={{ background: L ? "rgba(255,255,255,0.98)" : "rgba(15,23,42,0.98)", border: `1.5px solid ${L ? "rgba(249,115,22,0.15)" : "rgba(249,115,22,0.2)"}`, boxShadow: "0 15px 50px rgba(0,0,0,0.35)", backdropFilter: "blur(20px)" }}>
                <a href="/" onClick={() => setServicesOpen(false)} className="flex items-center gap-3 px-5 py-4 text-base font-semibold transition-colors"
                  style={{ color: L ? "#1e293b" : "#f1f5f9", borderBottom: `1px solid ${L ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)"}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = L ? "rgba(249,115,22,0.05)" : "rgba(249,115,22,0.08)"; e.currentTarget.style.color = "#f97316"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = L ? "#1e293b" : "#f1f5f9"; }}>
                  <span className="text-lg">📱</span> {t.nav.chipMobile}
                </a>
                <a href="/wifi" onClick={() => setServicesOpen(false)} className="flex items-center gap-3 px-5 py-4 text-base font-semibold transition-colors"
                  style={{ color: L ? "#1e293b" : "#f1f5f9" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = L ? "rgba(249,115,22,0.05)" : "rgba(249,115,22,0.08)"; e.currentTarget.style.color = "#f97316"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = L ? "#1e293b" : "#f1f5f9"; }}>
                  <span className="text-lg">📶</span> {t.nav.wifiService}
                </a>
              </div>
            )}
          </div>
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className="px-5 py-2.5 rounded-xl text-base font-semibold transition-all duration-300 hover:scale-[1.04] whitespace-nowrap"
              style={{ color: L ? "rgba(0,0,0,0.8)" : "#ffffff" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = L ? "#ea580c" : "#f97316"; e.currentTarget.style.background = L ? "rgba(249,115,22,0.06)" : "rgba(249,115,22,0.1)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = L ? "rgba(0,0,0,0.8)" : "#ffffff"; e.currentTarget.style.background = "transparent"; }}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="/portal/login"
            data-testid="navbar-portal"
            className="relative inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-bold text-white overflow-hidden transition-all duration-300 hover:scale-[1.03] whitespace-nowrap"
            style={{
              background: "linear-gradient(135deg, #dc2626, #ea580c, #f97316)",
              boxShadow: "0 4px 20px -3px rgba(234,88,12,0.5), inset 0 1px 0 rgba(255,255,255,0.15)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 6px 30px -3px rgba(234,88,12,0.6)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px -3px rgba(234,88,12,0.5)"; }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" /></svg>
            {t.nav.portal}
          </a>
          <a
            href="/crm/login"
            data-testid="navbar-crm"
            className={`relative inline-flex items-center gap-2 px-4 py-3 rounded-2xl text-xs font-semibold overflow-hidden transition-all duration-300 hover:scale-[1.03] whitespace-nowrap ${L ? "text-slate-600" : "text-slate-400"}`}
            style={{
              background: L ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${L ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`,
            }}
          >
            {t.nav.crm}
          </a>
          <LangSelector />
          <ThemeToggle />
        </div>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2"
          style={{ color: L ? "#334155" : "#fff" }}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden backdrop-blur-xl border-b overflow-hidden ${L ? "bg-white/95 border-black/5" : "bg-slate-950/95 border-white/10"}`}
          >
            <div className="px-4 py-5 flex flex-col gap-3">
              {/* Language + Theme at top */}
              <div className="flex items-center justify-between pb-3 mb-1" style={{ borderBottom: `1px solid ${L ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)"}` }}>
                <LangSelector />
                <ThemeToggle />
              </div>
              {/* Services links mobile */}
              <p className={`text-xs font-bold uppercase tracking-wider ${L ? "text-slate-400" : "text-slate-500"} mt-1`}>{t.nav.services}</p>
              <a href="/" className={`text-base ${L ? "text-slate-600 hover:text-slate-900" : "text-slate-300 hover:text-white"} transition-colors py-1 pl-3`}>📱 {t.nav.chipMobile}</a>
              <a href="/wifi" className={`text-base ${L ? "text-slate-600 hover:text-slate-900" : "text-slate-300 hover:text-white"} transition-colors py-1 pl-3`}>📶 {t.nav.wifiService}</a>
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={`text-base ${L ? "text-slate-600 hover:text-slate-900" : "text-slate-300 hover:text-white"} transition-colors py-2`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/portal/login"
                className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-2xl text-sm font-bold text-white"
                style={{ background: "linear-gradient(135deg, #dc2626, #ea580c, #f97316)", boxShadow: "0 4px 20px -3px rgba(234,88,12,0.5)" }}
              >
                {t.nav.portal}
              </a>
              <a
                href="/crm/login"
                className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-sm font-medium ${L ? "text-slate-600" : "text-slate-400"}`}
                style={{ background: L ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.04)", border: `1px solid ${L ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}` }}
              >
                {t.nav.crm}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
